import * as yup from 'yup';
import { round, sumBy } from 'lodash';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { DivisionType, InboundHoaInvoiceVatCode, Unity, VatCode } from '~/common/enums';
import BilledStatus from '~/common/enums/billedStatus';
import type { Nullable } from '~/common/types/index';
import type { Performance } from '@/types/trpc/hoa';

export type PerformanceShare = {
  unitId: string;
  buildingRelationId: Nullable<string>;
  share: number;
};

export type IndividualDivision = {
  amount: number;
  buildingRelationId: string;
  percentage: string | null;
  share: number;
  unitId: string;
};

const MAX_PERCENTAGE_ROUNDING_DIFFERENCE = 0.01;

export type PerformanceFormFields = {
  amount: number;
  bookYearId: string | null;
  contractId: string | null;
  contractPerformanceId: string | null;
  date: string;
  description: string | null;
  divisionKeyId: string | null;
  divisionType: DivisionType | null;
  homeownerAssociationGeneralLedgerAccountId: string | null;
  homeownerAssociationId: string | null;
  individualDivisions?: IndividualDivision[];
  shares: PerformanceShare[];
  status: BilledStatus | null;
  total: number | null;
  unitPrice: number | null;
  unity: Unity;
  vat: InboundHoaInvoiceVatCode;
};

const usePerformanceFormikConfig = ({
  initialPerformance,
  initialHoaId,
}: {
  initialPerformance?: Performance | null;
  initialHoaId?: string | null;
}) => {
  // also used in toolbar actions, so no guarantee this will be defined
  const { homeownerAssociationId } = useParams();
  const { t } = useTranslation('errors');

  const initialValues = useMemo(() => {
    const defaultInitialValues = {
      amount: 1,
      bookYearId: null,
      contractId: null,
      contractPerformanceId: null,
      date: dayjs().format('YYYY-MM-DD'),
      description: null,
      divisionKeyId: null,
      divisionType: DivisionType.Amount,
      homeownerAssociationGeneralLedgerAccountId: null,
      homeownerAssociationId: homeownerAssociationId || initialHoaId || null,
      individualDivisions: [],
      shares: [],
      status: BilledStatus.NotBilled,
      total: null,
      unitPrice: null,
      unity: Unity.Piece,
      vat: InboundHoaInvoiceVatCode.PurchaseServices21,
    };

    if (!initialPerformance) return defaultInitialValues;

    const totalShareAmount = sumBy(initialPerformance.shares, (share) => share.share);
    const totalAmount = initialPerformance.unitPrice * initialPerformance.amount;

    const initialIndividualDivisions = initialPerformance.shares.map(
      ({ buildingRelationId, share, unitId }) => {
        const amount = round((totalAmount / totalShareAmount) * share, 2);
        const percentage = round((amount / totalAmount) * 100, 4);

        return {
          amount,
          buildingRelationId,
          percentage,
          share,
          unitId,
        };
      },
    );

    return {
      ...defaultInitialValues,
      ...initialPerformance,
      individualDivisions: initialIndividualDivisions,
      total: initialPerformance.totalPrice,
      unity: initialPerformance.unity || Unity.Piece,
      vat: initialPerformance.vat || VatCode.InlandExclusive21,
    };
  }, [homeownerAssociationId, initialHoaId, initialPerformance]);

  const validationSchema = yup.object().shape(
    {
      amount: yup.number().nullable().required(t('fieldIsRequired')),
      bookYearId: yup.string().nullable().required(t('fieldIsRequired')),
      contractId: yup.string().nullable().required(t('fieldIsRequired')),
      contractPerformanceId: yup.string().nullable().required(t('fieldIsRequired')),
      date: yup.string().nullable().required(t('fieldIsRequired')),
      description: yup.string().nullable().required(t('fieldIsRequired')),
      divisionKeyId: yup
        .string()
        .nullable()
        .test('fieldIsInvalid', t('fieldIsInvalid'), (value, ctx) => {
          if (value && !ctx.parent.individualDivisions.length) return true;

          const totalPercentage = sumBy(
            ctx.parent.individualDivisions,
            (division: IndividualDivision) =>
              division.percentage ? parseFloat(division.percentage) || 0 : 0,
          );

          return Math.abs(totalPercentage - 100) <= MAX_PERCENTAGE_ROUNDING_DIFFERENCE;
        })
        .test('relationsMissing', t('relationsMissing'), (divisionKeyValue, ctx) => {
          if (divisionKeyValue) return true;

          return ctx.parent.individualDivisions.every(
            (division: IndividualDivision) => !!division.buildingRelationId,
          );
        }),
      homeownerAssociationGeneralLedgerAccountId: yup.string().nullable(),
      homeownerAssociationId: yup.string().nullable().required(t('fieldIsRequired')),
      total: yup.string().required(t('fieldIsRequired')),
      unitPrice: yup.string().nullable().required(t('fieldIsRequired')),
      unity: yup.string().nullable().required(t('fieldIsRequired')),
      vat: yup.string().nullable().required(t('fieldIsRequired')),
    },
    [['useDefaultSavingsAccount', 'useDefaultCurrentAccount']],
  );

  return {
    enableReinitialize: true,
    initialValues,
    validationSchema,
  };
};

export default usePerformanceFormikConfig;
